import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Sun, Moon, PhoneCall } from 'lucide-react';
import { useState, useEffect } from 'react';
import { Disclosure } from '@headlessui/react';
import { motion } from 'framer-motion';

const navigation = [
  { name: 'Overview', href: '/' },
  { name: 'Packages', href: '/services' },
  { name: 'Gallery', href: '/gallery' },
  { name: 'Bookings', href: '/contact' },
];

export default function Navbar() {
  const [theme, setTheme] = useState(
    () => (typeof window !== 'undefined' && localStorage.getItem('theme')) || 'light',
  );
  const location = useLocation();

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    if (typeof window !== 'undefined') {
      localStorage.setItem('theme', theme);
    }
  }, [theme]);

  return (
    <Disclosure
      as="nav"
      className="sticky top-0 z-50 border-b border-white/5 bg-[#14080b]/90 backdrop-blur-xl"
    >
      {({ open }) => (
        <>
          <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4">
            <div className="flex items-center gap-3">
              <Link to="/" className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-500 via-brand-400 to-rose-500 text-lg font-bold shadow-lg shadow-brand-500/30">
                  SM
                </div>
                <div>
                  <p className="font-display text-lg font-semibold text-white">
                    Auto Shazam
                  </p>
                  <p className="text-xs uppercase tracking-[0.25em] text-white/60">
                    On-site perfection
                  </p>
                </div>
              </Link>
            </div>

            <div className="hidden items-center gap-8 md:flex">
              {navigation.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className="relative text-sm font-medium uppercase tracking-wider text-white/70 transition-colors hover:text-white"
                  >
                    <span>{item.name}</span>
                    {isActive && (
                      <motion.span
                        layoutId="nav-underline"
                        className="absolute -bottom-1 left-0 right-0 h-[2px] rounded-full bg-brand-400"
                      />
                    )}
                  </Link>
                );
              })}
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-white/70 transition-colors hover:border-white/30 hover:text-white"
                aria-label="Toggle theme"
              >
                {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
              </button>

              <a
                href="tel:+16475550147"
                className="hidden items-center gap-2 rounded-full bg-brand-500 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-brand-500/30 transition hover:scale-[1.02] hover:bg-brand-400 md:flex"
              >
                <PhoneCall size={16} />
                Call / Text
              </a>

              <Disclosure.Button className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-white transition hover:border-white/40 md:hidden">
                {open ? <X size={18} /> : <Menu size={18} />}
              </Disclosure.Button>
            </div>
          </div>

          {/* Mobile Navigation */}
          <Disclosure.Panel className="md:hidden">
            <div className="space-y-4 px-4 pb-6 pt-4 text-sm">
              {navigation.map((item) => (
                <Disclosure.Button
                  key={item.name}
                  as={Link}
                  to={item.href}
                  className={`block rounded-2xl border border-white/10 px-4 py-3 font-medium capitalize tracking-wide ${
                    location.pathname === item.href
                      ? 'bg-white/10 text-white shadow-inner shadow-black/20'
                      : 'text-white/70 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  {item.name}
                </Disclosure.Button>
              ))}
              <Link
                to="/contact"
                className="flex items-center justify-center gap-2 rounded-2xl bg-brand-500 px-4 py-3 font-semibold text-white shadow-lg shadow-brand-500/30 transition hover:bg-brand-400"
              >
                <PhoneCall size={18} />
                Request a Detail
              </Link>
            </div>
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>
  );
}
